/**
 * Lifely App Version
 * Increment this when making changes:
 * - Major (x.0.0): Breaking changes, major features
 * - Minor (0.x.0): New features, significant updates  
 * - Patch (0.0.x): Bug fixes, small improvements
 */

export const APP_VERSION = '1.6.1';

export const VERSION_HISTORY = [
  {
    version: '1.6.1',
    date: '2026-04-05',
    changes: [
      'Added Cloudflare Turnstile protection to Emergency Access and Contact forms',
    ]
  },
  {
    version: '1.6.0',
    date: '2026-03-29',
    changes: [
      'Memberstack paid plan migration with custom auth domain',
      'Admin testing plans (Insights, Standard, Plus) added alongside production plans',
      'Dev/staging environment: separate Worker, D1 database, Cloudflare Pages preview',
      'CORS locked per environment (prod: app.lifely.guide, dev: dev.lifely-app.pages.dev)',
      'Security: stripped all debug console.log from frontend and backend',
      'Security: single-use emergency codes — revoked after any request (pending/denied/approved)',
      'Security: denial notification email sent to requester when access is denied',
      'Security: audit log in Settings showing all security events chronologically',
      'Security: Cloudflare WAF, SPF, DMARC, DKIM, TLS 1.2 minimum, Full (Strict) SSL',
      'Fixed: executor data in nominee export now properly parsed from JSON',
      'Fixed: email sender address corrected to noreply@mail.lifely.guide',
      'Fixed: email verification now auto-updates UI via polling (no need to reload)',
      'Fixed: used emergency code error message now explains single-use policy',
      'Updated emergency access UI, PDF card, and public form with single-use messaging',
    ]
  },
  {
    version: '1.5.1',
    date: '2026-03-21',
    changes: [
      'Fixed: Will review reminder emails now send correctly (was referencing undefined emailWrapper)',
      'Fixed: Escalation INSERT includes contact_email and contact_name columns',
      'Fixed: Export tokens INSERT includes created_at column',
      'Fixed: Empty string reminder_date no longer matches scheduler query',
      'Fixed: Account deletion now purges pending_escalations and export_tokens tables',
      'Security: CORS locked to app.lifely.guide and lifely.guide (was wildcard *)',
      'Security: Added HSTS, X-Frame-Options, CSP headers via Cloudflare Pages _headers',
      'Updated will reminder email: button links to /will-status, clarifies reminder is cleared after sending',
      'Updated Help page: check-in cycle, 24h follow-ups, 48h escalation, will review reminders, sharing explanation',
    ]
  },
  {
    version: '1.5.0',
    date: '2026-03-12',
    changes: [
      'Secure continuity escalation flow: 48-hour grace period before any data is shared to nominees',
      'Owner "last chance" deny link to cancel escalation',
      'Secure single-use nominee access page with filtered data export and PDF download',
      'Admin notification email on escalation',
      'Will review reminders now send emails via the scheduled cron job',
      'Alternative email support for will reminders',
      'Follow-up reminders after a missed check-in are now sent every 24 hours',
      'Clear step-by-step explanation of the check-in cycle in Continuity Settings',
      'UI warnings about escalation flow on Continuity Wizard, Settings, and Sharing pages',
    ]
  },
  {
    version: '1.4.0',
    date: '2026-02-28',
    changes: [
      'Continuity scheduler overhaul: timezone-aware, handles overdue users, improved logging',
      'Liabilities & Debts map (12th map type) with dashboard 12-tile layout',
      'Comprehensive PDF/CSV export with legal disclaimers and "I understand" modal',
      'User avatar in navigation bar and settings page',
      'Emergency Access QR code print-to-PDF feature',
      'Renamed "Continuity" to "Continuity Heartbeat" throughout the app',
      'Fixed nominee count showing zero after page refresh',
      'Updated in-app Help documentation with new screenshots',
    ]
  },
  {
    version: '1.3.0',
    date: '2025-12-15',
    changes: [
      'Dashboard state persistence fixes',
      'Navigation reorder and progress stepper',
      'Simplified Continuity UX with wizard and settings split',
      'Timezone support for check-in reminders',
      'Formal Arrangements tabbed layout',
      'Emergency Access feature with 6-character code and QR',
      'Comprehensive Export page',
    ]
  },
  {
    version: '1.2.0',
    date: '2025-02-20',
    changes: [
      'Added Terms of Service notice to Memberstack signup/login modals',
      'Added Email Verification feature for continuity setup',
      'Fixed: Alternative email now syncs to database (was localStorage only)',
      'Fixed: Alternative email no longer shared across users (critical bug fix)',
      'Fixed: New accounts no longer show "Continuity is Active" by default',
      'Fixed: Account deletion uses correct API URL in production',
      'Fixed: Verification email uses alternative email if configured',
    ]
  },
  {
    version: '1.1.5',
    date: '2025-02-14',
    changes: [
      'Fixed: Existing users logging in via plan buttons no longer redirected to Stripe',
      'Added plan check before checkout - users with existing plans go to dashboard',
      'Improved signup flow to detect new vs existing users',
    ]
  },
  {
    version: '1.1.4',
    date: '2025-02-14',
    changes: [
      'Added redirect to dashboard after login from /join/pricing',
      'Implemented 14-day Insights trial expiration logic',
      'Auto-removes Insights plan when trial expires',
      'Expired users redirected to /join/pricing to upgrade',
    ]
  },
  {
    version: '1.1.3',
    date: '2025-02-14',
    changes: [
      'Fixed null reference error in WillStatus.jsx when willStatus is not yet loaded',
      'Added null safety to all useState initializers using status fallback object',
    ]
  },
  {
    version: '1.1.2',
    date: '2025-02-14',
    changes: [
      'Fixed Memberstack v2 onAuthChange callback (receives member object, not payload)',
      'Fixed auto-login to pass full member data including planConnections',
      'Plan gating should now work correctly after signup',
    ]
  },
  {
    version: '1.1.1',
    date: '2025-02-14',
    changes: [
      'Added /join/pricing page for signup and subscription flow',
      'Upgraded Memberstack to v2 with cross-subdomain cookie support',
      'Public pricing page with plan selection and Memberstack checkout',
      'Upgrade redirect now goes to /join/pricing',
    ]
  },
  {
    version: '1.1.0',
    date: '2025-02-07',
    changes: [
      'Upgrade plan button redirects to pricing page',
    ]
  },
  {
    version: '1.0.7',
    date: '2025-02-07',
    changes: [
      'Added Memberstack plan-based gated content',
      'Insights plan: Dashboard, Insights, Settings only',
      'Standard/Plus plans: Full access to all features',
      'Upgrade prompts for restricted content',
    ]
  },
  {
    version: '1.0.6',
    date: '2025-02-07',
    changes: [
      'Added Password Manager Digital Legacy guides: 1Password, LastPass, and Bitwarden',
      'Includes emergency access setup, security considerations, and step-by-step instructions',
    ]
  },
  {
    version: '1.0.5',
    date: '2025-02-07',
    changes: [
      'Added Insights page with estate planning guides for US, UK (England & Wales, Scotland, Northern Ireland), Canada, and Australia',
      'Includes legal requirements, will-making steps, and jurisdiction-specific information',
    ]
  },
  {
    version: '1.0.4',
    date: '2025-02-07',
    changes: [
      'Updated map images: Crypto, Art, Social Media, Pets, Property, Physical Records, Vehicles, Shares',
    ]
  },
  {
    version: '1.0.3',
    date: '2025-02-07',
    changes: [
      'Updated design system to match marketing website',
      'New color palette: warm terracotta/coral (#C4836A)',
      'New fonts: Fraunces (headings) + DM Sans (body)',
      'Updated backgrounds, borders to warm beige tones',
    ]
  },
  {
    version: '1.0.2',
    date: '2025-02-07',
    changes: [
      'Will Status card now shows blue when not configured (consistent with Maps Status)',
    ]
  },
  {
    version: '1.0.1',
    date: '2025-02-07',
    changes: [
      'Fixed dashboard Maps Status card color (blue when no entries, green when configured)',
      'Fixed map count to show 11 maps (excludes Formal Arrangements)',
      'Fixed Your Wishes notes being lost when saved without selecting a contact',
    ]
  },
  {
    version: '1.0.0',
    date: '2025-02-06',
    changes: [
      'Initial versioned release',
      'Account deletion with email confirmation',
      'Continuity check-in system with Resend emails',
      'Cron-based reminder scheduling',
      'Full D1 database integration',
      'Memberstack authentication',
    ]
  }
];
